import React, { useEffect, useState } from "react";
import moment from "moment";
import numeral from "numeral";
import _, { isNumber } from "lodash";
import { v4 as uuidV4 } from "uuid";
import { Grid, FormControlLabel, Radio } from "@material-ui/core";
import { ButtonText, LabelText } from "@loanbossinc/component-library";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";

import { PaddedWrapper, StyledRadioGroup, RentContainer, RentRow, StyledRentsInput, StyledHeaderName, RowContainer, StyledBaseRentInput } from "./styles";

const radioButtonsOptions = [
  { value: "month", label: "Per Month" },
  { value: "year", label: "Per Year" }
];

const MONTHS_IN_YEAR = 12;

const ErrorMessage = ({ message }) => (
  <div style={{ position: "absolute", color: "#FF1744", fontFamily: "Work Sans", fontSize: "9px", top: "-10px", left: "-5px" }}>{message}</div>
);

const ScheduledRentRow = props => {
  const { id, footage, startDate, endDate, monthlyAmount, period, editingSchedule, handleInputChange, handleDeleteRow, validationErrors } = props;

  const startDateFormatted = moment.utc(startDate).format("MM/DD/YY");
  const endDateFormatted = moment.utc(endDate).format("MM/DD/YY");
  const editStartDateToShow = startDate ? moment.utc(startDate).format("YYYY-MM-DD") : "";
  const editEndDateToShow = endDate ? moment.utc(endDate).format("YYYY-MM-DD") : "";
  const amountBasedOnPeriod = period === "month" ? monthlyAmount : monthlyAmount * MONTHS_IN_YEAR;
  const amountToShow = amountBasedOnPeriod ? numeral(amountBasedOnPeriod).format("$0,0.00") : "";
  const perSqFootBasedOnPeriod = period === "month" ? monthlyAmount / footage : (monthlyAmount / footage) * MONTHS_IN_YEAR;
  const perSqFootToShow = perSqFootBasedOnPeriod ? numeral(perSqFootBasedOnPeriod).format("$0,0.00") : "";

  const chargeAmountInputValue = amountBasedOnPeriod ? Math.round(amountBasedOnPeriod * 100) / 100 : null;

  const errorMessageStartDate = validationErrors[id] && validationErrors[id][0];
  const errorMessageEndDate = validationErrors[id] && validationErrors[id][1];

  return (
    <RentContainer style={{ position: "relative" }}>
      <RentRow w="26%" align="start">
        {!editingSchedule ? (
          startDateFormatted
        ) : (
          <div style={{ position: "relative" }}>
            {errorMessageStartDate && <ErrorMessage message={errorMessageStartDate} />}
            <StyledRentsInput
              name="startDate"
              label=""
              type="date"
              defaultValue={editStartDateToShow}
              onChange={handleInputChange}
              textAlign="left"
              inputProps={{ max: "9999-12-31" }}
              isError={editingSchedule && errorMessageStartDate}
            />
          </div>
        )}
      </RentRow>
      <RentRow w="26%" align="start">
        {!editingSchedule ? (
          endDateFormatted
        ) : (
          <div style={{ position: "relative" }}>
            {errorMessageEndDate && <ErrorMessage message={errorMessageEndDate} />}
            <StyledRentsInput
              name="endDate"
              label=""
              type="date"
              defaultValue={editEndDateToShow}
              onChange={handleInputChange}
              textAlign="left"
              inputProps={{ max: "9999-12-31" }}
              isError={editingSchedule && errorMessageEndDate}
            />
          </div>
        )}
      </RentRow>
      <RentRow w="20%" align="center">
        {!editingSchedule ? (
          amountToShow
        ) : (
          <StyledBaseRentInput
            onChange={handleInputChange}
            value={chargeAmountInputValue}
            textAlign="center"
            name="monthlyAmount"
            allowNegative={false}
            decimals={2}
            thousandSeparator
          />
        )}
      </RentRow>
      <RentRow w="20%" align="center">
        {`${perSqFootToShow} /${period}`}
      </RentRow>
      <RentRow w="8%" align="right">
        <DeleteIcon
          style={{
            color: editingSchedule ? "#005AA9" : "#9e9e9e",
            position: "absolute",
            right: "5px",
            top: "15px",
            cursor: editingSchedule && "pointer"
          }}
          onClick={editingSchedule ? handleDeleteRow : undefined}
        />
      </RentRow>
    </RentContainer>
  );
};

const ScheduledRents = props => {
  const { leaseState, setLeaseState, scheduledRents } = props;
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduledRentsCopy, setScheduledRentsCopy] = useState([]);
  const [period, setPeriod] = useState("month");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (scheduledRents?.length) {
      setScheduledRentsCopy(_.cloneDeep(scheduledRents));
    }
  }, [scheduledRents]);

  const handleRadioChange = e => {
    setPeriod(e.target.value);
  };

  const validateDateInputs = (date, rentId, type) => {
    if (moment.utc(date).isValid()) {
      let errorMessage = null;

      const activeRent = scheduledRentsCopy.find(rent => rent.id === rentId);

      // check if start is earlier than end
      if (type === "startDate" && moment.utc(activeRent.endDate).isValid()) {
        if (moment.utc(date).isSameOrAfter(moment.utc(activeRent.endDate), "day")) {
          errorMessage = "Start must be before end";
        }
      } else {
        // check if end is later than start
        if (moment.utc(activeRent.startDate).isValid() && moment.utc(date).isSameOrBefore(moment.utc(activeRent.startDate), "day")) {
          errorMessage = "End must be after start";
        }
      }

      // check if overlaps with other rent
      scheduledRentsCopy.forEach(rent => {
        const isOverlapping = moment.utc(date).isBetween(moment.utc(rent.startDate), moment.utc(rent.endDate), "day", "()");
        if (rent.id !== rentId && isOverlapping && !errorMessage) {
          errorMessage = "Date overlaps with another rent";
        }
      });

      // check if overlaps with lease start/end
      if (!errorMessage) {
        const leaseStart = moment.utc(leaseState.leaseStartDate);
        const leaseEnd = moment.utc(leaseState.leaseEndDate);
        const withinLeaseTerm = moment.utc(date).isBetween(leaseStart, leaseEnd, "day", "[]");
        if (!withinLeaseTerm) {
          errorMessage = "Date is not within lease term";
        }
      }

      const activeErrorsArray = validationErrors[rentId] || [null, null];
      if (type === "startDate") {
        activeErrorsArray[0] = errorMessage;
      } else {
        activeErrorsArray[1] = errorMessage;
      }
      setValidationErrors({ ...validationErrors, [rentId]: activeErrorsArray });
    }
  };

  const handleInputChange = (e, rentId) => {
    const { name, value } = e.target;
    if (name && value !== undefined) {
      setScheduledRentsCopy(
        scheduledRentsCopy.map(rent => {
          if (rent.id !== rentId) {
            return rent;
          }
          if (name === "startDate" || name === "endDate") {
            validateDateInputs(value, rentId, name);

            return {
              ...rent,
              [name]: moment.utc(value).toISOString()
            };
          }
          let amount = parseFloat(value?.replace(/,/g, ""));
          if (period === "year") {
            amount = amount / MONTHS_IN_YEAR;
          }

          return {
            ...rent,
            monthlyAmount: amount
          };
        })
      );
    }
  };

  const handleAddRow = () => {
    if (!editingSchedule) {
      setEditingSchedule(true);
      setLeaseState(prev => ({
        ...prev,
        isSuiteSectionValid: false
      }));
    }
    const emptyRow = {
      id: uuidV4(),
      chargeCode: "rentbase",
      chargeType: "Rent",
      endDate: null,
      startDate: null,
      monthlyAmount: null,
      manuallyAdded: true,
      squareFeet: leaseState.squareFootage
    };
    setScheduledRentsCopy([...scheduledRentsCopy, emptyRow]);
  };

  const handleDeleteRow = id => {
    setScheduledRentsCopy([...scheduledRentsCopy.filter(rent => rent.id !== id)]);
  };

  const handleSaveSchedule = () => {
    setEditingSchedule(false);
    setLeaseState(prev => ({
      ...prev,
      isSuiteSectionValid: true,
      isAnythingWasUpdated: true,
      rentsAndCharges: [...prev.chargeSchedules, ...scheduledRentsCopy]
    }));
  };

  const checkIfNoErrors = () => scheduledRentsCopy.every(rent => {
    const errors = validationErrors[rent.id];

    return !errors?.some(error => Boolean(error));
  });

  const areLeaseDatesValid = Boolean(leaseState.leaseStartDate) && Boolean(leaseState.leaseEndDate);

  const areRowsValid = !scheduledRentsCopy.length ||
    (scheduledRentsCopy.length &&
      scheduledRentsCopy.every(rent => {
        const isRentEntered = isNumber(rent.monthlyAmount) && !Number.isNaN(rent.monthlyAmount);
        const areDatesEntered = moment(rent.startDate).isValid() && moment(rent.endDate).isValid();

        return isRentEntered && areDatesEntered;
      }));

  const isAbleToSave = checkIfNoErrors() && areLeaseDatesValid && areRowsValid;

  const getScheduleAction = () => {
    if (!editingSchedule && !scheduledRentsCopy.length) {
      return null;
    }
    if (editingSchedule) {
      return (
        <ButtonText
          label={
            <div style={{ display: "flex" }}>
              <div style={{ marginRight: "0.3em" }}>Save Schedule</div>
              <CheckIcon style={{ cursor: "pointer", color: !isAbleToSave ? "#9aa2a8" : "#005AA9" }} />
            </div>
          }
          disabled={!isAbleToSave}
          onClick={handleSaveSchedule}
        />
      );
    }

    return (
      <ButtonText
        label={
          <div style={{ display: "flex" }}>
            <div style={{ marginRight: "0.3em" }}>Edit Schedule</div>
            <EditIcon style={{ cursor: "pointer", color: !isAbleToSave ? "#9aa2a8" : "#005AA9" }} />
          </div>
        }
        onClick={() => {
          setEditingSchedule(true);
          setLeaseState(prev => ({
            ...prev,
            isSuiteSectionValid: false
          }));
        }}
        disabled={!isAbleToSave}
      />
    );
  };

  const getAddNewScheduleRow = isIconNeeded => {
    if (!editingSchedule && scheduledRentsCopy.length) {
      return null;
    }

    return (
      <RentContainer>
        <RentRow w="80%" align="left">
          <ButtonText onClick={handleAddRow} label="Add scheduled rent" />
        </RentRow>
        {isIconNeeded && (
          <RentRow w="20%" align="right">
            <EditIcon style={{ cursor: "pointer", color: "#005AA9" }} onClick={handleAddRow} />
          </RentRow>
        )}
      </RentContainer>
    );
  };

  return (
    <RowContainer>
      <Grid container justify="flex-start" style={{ marginBottom: "10px" }} alignItems="center" direction="row">
        <Grid item xs={12}>
          <PaddedWrapper>
            <StyledHeaderName>SCHEDULED RENTS</StyledHeaderName>
          </PaddedWrapper>
          <PaddedWrapper>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <StyledRadioGroup value={period} aria-label="year-or-month" name="period" onChange={handleRadioChange}>
                {radioButtonsOptions.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={<LabelText value={option.label} />}
                  />
                ))}
              </StyledRadioGroup>
              {getScheduleAction()}
            </div>
          </PaddedWrapper>
        </Grid>
        <Grid item xs={12}>
          <PaddedWrapper>
            <RentContainer header>
              <RentRow w="26%" align="start" bold>
                START
              </RentRow>
              <RentRow w="26%" align="start" bold>
                END
              </RentRow>
              <RentRow w="20%" align="center" bold>
                BASE RENT
              </RentRow>
              <RentRow w="20%" align="center" bold>
                RENT PER SQ FT
              </RentRow>
              <RentRow w="8%" align="right" />
            </RentContainer>
            {!editingSchedule && getAddNewScheduleRow(true)}
          </PaddedWrapper>
        </Grid>
        <Grid item xs={12}>
          <PaddedWrapper>
            {scheduledRentsCopy.map(rent => (
              <ScheduledRentRow
                key={rent.id}
                id={rent.id}
                editingSchedule={editingSchedule}
                period={period}
                handleInputChange={e => handleInputChange(e, rent.id)}
                handleDeleteRow={() => handleDeleteRow(rent.id)}
                footage={rent.squareFeet}
                startDate={rent.startDate}
                endDate={rent.endDate}
                monthlyAmount={rent.monthlyAmount}
                validationErrors={validationErrors}
              />
            ))}
            {scheduledRentsCopy.length ? getAddNewScheduleRow(false) : null}
            {editingSchedule && !scheduledRentsCopy.length ? getAddNewScheduleRow(false) : null}
          </PaddedWrapper>
        </Grid>
      </Grid>
    </RowContainer>
  );
};

export default ScheduledRents;
